import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { FiShield, FiSearch, FiFilter, FiRefreshCw, FiLogIn, FiLogOut, FiEdit, FiTrash2, FiPlus, FiSend, FiUser, FiDatabase, FiCalendar } from 'react-icons/fi';

const ACTION_META = {
  LOGIN: { icon: <FiLogIn />, color: '#7ea52e', bg: '#eef3e6', label: 'Login' },
  LOGOUT: { icon: <FiLogOut />, color: '#64748b', bg: '#f1f5f9', label: 'Logout' },
  LOGIN_FAILED: { icon: <FiLogIn />, color: '#ef4444', bg: '#fef2f2', label: 'Failed Login' },
  CREATE_USER: { icon: <FiPlus />, color: '#22c55e', bg: '#dcfce7', label: 'User Created' },
  UPDATE_USER: { icon: <FiEdit />, color: '#f97316', bg: '#fff7ed', label: 'User Updated' },
  DELETE_USER: { icon: <FiTrash2 />, color: '#ef4444', bg: '#fef2f2', label: 'User Deleted' },
  CREATE_CAMPAIGN: { icon: <FiPlus />, color: '#1a1a18', bg: '#f0f0ed', label: 'Campaign Created' },
  UPDATE_CAMPAIGN: { icon: <FiEdit />, color: '#f97316', bg: '#fff7ed', label: 'Campaign Updated' },
  DELETE_CAMPAIGN: { icon: <FiTrash2 />, color: '#ef4444', bg: '#fef2f2', label: 'Campaign Deleted' },
  SEND_MESSAGE: { icon: <FiSend />, color: '#22c55e', bg: '#dcfce7', label: 'Message Sent' },
  UPLOAD_DATA: { icon: <FiDatabase />, color: '#06b6d4', bg: '#ecfeff', label: 'Data Upload' },
  SCHEDULE: { icon: <FiCalendar />, color: '#8b5cf6', bg: '#ede9fe', label: 'Scheduled' },
};

const getActionMeta = (action) => {
  const key = Object.keys(ACTION_META).find(k => action?.toUpperCase().includes(k));
  return ACTION_META[key] || { icon: <FiShield />, color: '#64748b', bg: '#f1f5f9', label: action };
};

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const { data } = await api.get('/admin/audit-logs', { params });
      setLogs(data.logs || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [page, actionFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  /* Stats from loaded logs */
  const todayLogs = logs.filter(l => new Date(l.createdAt).toDateString() === new Date().toDateString()).length;
  const loginLogs = logs.filter(l => l.action?.includes('LOGIN') && !l.action?.includes('FAILED')).length;
  const failedLogs = logs.filter(l => l.action?.includes('FAILED')).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Logs</h1>
          <p className="page-subtitle">Complete activity trail — who did what, when</p>
        </div>
        <button className="btn btn-outline" onClick={() => { setPage(1); fetchLogs(); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {/* ── Quick Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-card-icon purple"><FiShield /></div>
          <div className="stat-card-value">{total.toLocaleString()}</div>
          <div className="stat-card-label">Total Events</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon blue"><FiCalendar /></div>
          <div className="stat-card-value">{todayLogs}</div>
          <div className="stat-card-label">Today's Events</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon green"><FiLogIn /></div>
          <div className="stat-card-value">{loginLogs}</div>
          <div className="stat-card-label">Logins (this page)</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon red"><FiShield /></div>
          <div className="stat-card-value">{failedLogs}</div>
          <div className="stat-card-label">Failed Actions</div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7ea52e', fontSize: 15 }}><FiFilter /></div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>Filters</h3>
        </div>
        <form onSubmit={handleSearch} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label"><FiSearch style={{ marginRight: 4, verticalAlign: 'middle' }} />Search</label>
            <input type="text" className="form-input" placeholder="Username, action, details..." value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Action Type</label>
            <select className="form-select" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}>
              <option value="">All Actions</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="SEND">Send</option>
              <option value="UPLOAD">Upload</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">From</label>
            <input type="date" className="form-input" value={dateRange.start}
              onChange={e => setDateRange({ ...dateRange, start: e.target.value })} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">To</label>
            <input type="date" className="form-input" value={dateRange.end}
              onChange={e => setDateRange({ ...dateRange, end: e.target.value })} />
          </div>
          <button type="submit" className="btn btn-primary" style={{ height: 44 }}>
            <FiSearch />
          </button>
        </form>
      </div>

      {/* ── Logs Timeline ── */}
      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><FiShield /></div>
            <h3>No audit logs found</h3>
            <p>Adjust your filters or check back later</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {logs.map((log, i) => {
                const meta = getActionMeta(log.action);
                return (
                  <div key={log._id || i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0',
                    borderBottom: i < logs.length - 1 ? '1px solid var(--border-color)' : 'none',
                  }}>
                    {/* Icon */}
                    <div style={{
                      width: 38, height: 38, minWidth: 38, borderRadius: 10,
                      background: meta.bg, color: meta.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                    }}>{meta.icon}</div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{log.username || 'System'}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                          background: meta.bg, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.5,
                        }}>{meta.label}</span>
                      </div>
                      {log.details && <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.5 }}>{log.details}</p>}
                      {log.ipAddress && <span style={{ fontSize: 11, color: '#94a3b8' }}>IP: {log.ipAddress}</span>}
                    </div>

                    {/* Time */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{timeAgo(log.createdAt)}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(log.createdAt).toLocaleTimeString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, borderTop: '1px solid var(--border-color)', marginTop: 4 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                Page {page} of {totalPages} ({total.toLocaleString()} events)
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                <button className="btn btn-sm btn-primary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
